import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type ColumnDef } from "@tanstack/react-table";

import { DialogoAlerta } from "@/components/alert-dialog";
import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGlobalLoader } from "@/components/GlobalLoader";

import { useOrdensDeImpressao } from "@/contexts/OrdensDeImpressaoContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { alert } from "@/contexts/AlertModalContext";

import {
  Braces,
  CheckCircle2,
  ClipboardList,
  Copy,
  Inbox,
  Loader2,
  Printer,
  RefreshCw,
  Trash2,
} from "lucide-react";

type Ordem = {
  id: string;
  "nome-da-ordem": string;
};

type AlvoApagar =
  | { tipo: "uma"; ordem: string }
  | { tipo: "selecionadas" }
  | { tipo: "todas" };

function OrdensDeImpressao() {
  const { t } = useTranslation();
  const { isLoading } = useGlobalLoader();
  const {
    definirOrdemComoImpressa,
    definirOrdensComoImpressas,
    ordens,
    ordensImpressas,
    coletarOrdens,
    coletarOrdensImpressas,
    obterOrdemBruta,
  } = useOrdensDeImpressao();
  const [alvoApagar, setAlvoApagar] = useState<AlvoApagar | null>(null);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [ordemJson, setOrdemJson] = useState<string | null>(null);
  const [conteudoJson, setConteudoJson] = useState<string>("");
  const [carregandoJson, setCarregandoJson] = useState(false);
  const [impressasAberto, setImpressasAberto] = useState(false);
  const { coletaCategorias } = useCategorias();
  const { coletaSeriais } = useSeriais();
  const { coletaLocalizacoes } = useLocalizacoes();
  const navigate = useNavigate();

  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletarOrdens();
    await coletaLocalizacoes();
    alert.success(t("ordens.syncSuccess"));
  };

  const handleApagarOrdem = (ordem: string) => {
    setAlvoApagar({ tipo: "uma", ordem });
  };

  const abrirJson = async (ordem: string) => {
    setOrdemJson(ordem);
    setConteudoJson("");
    setCarregandoJson(true);
    try {
      const dados = await obterOrdemBruta(ordem);
      setConteudoJson(JSON.stringify(dados, null, 2));
    } catch (error) {
      console.error("Erro ao coletar JSON da ordem:", error);
      alert.error(t("ordens.jsonError"));
      setOrdemJson(null);
    } finally {
      setCarregandoJson(false);
    }
  };

  const copiarJson = async () => {
    try {
      await navigator.clipboard.writeText(conteudoJson);
      alert.success(t("ordens.jsonCopied"));
    } catch {
      alert.error(t("ordens.jsonCopyError"));
    }
  };

  const abrirImpressas = async () => {
    setImpressasAberto(true);
    await coletarOrdensImpressas();
  };

  const toggleSelecionada = (ordem: string, checked: boolean) => {
    setSelecionadas((prev) => {
      const next = new Set(prev);
      if (checked) next.add(ordem);
      else next.delete(ordem);
      return next;
    });
  };

  const confirmarApagar = () => {
    if (!alvoApagar) return;
    if (alvoApagar.tipo === "uma") {
      definirOrdemComoImpressa(alvoApagar.ordem);
    } else if (alvoApagar.tipo === "selecionadas") {
      definirOrdensComoImpressas([...selecionadas]);
      setSelecionadas(new Set());
    } else {
      definirOrdensComoImpressas(ordens);
      setSelecionadas(new Set());
    }
  };

  const tituloApagar =
    alvoApagar?.tipo === "selecionadas"
      ? t("ordens.deleteSelectedTitle")
      : alvoApagar?.tipo === "todas"
        ? t("ordens.deleteAllTitle")
        : t("ordens.deleteTitle");

  const descricaoApagar =
    alvoApagar?.tipo === "selecionadas"
      ? t("ordens.deleteSelectedConfirm", { count: selecionadas.size })
      : alvoApagar?.tipo === "todas"
        ? t("ordens.deleteAllConfirm", { count: ordens.length })
        : t("ordens.deleteConfirm");

  const columns: ColumnDef<Ordem>[] = [
    {
      id: "select",
      header: () => (
        <Checkbox
          checked={
            ordens.length > 0 && selecionadas.size === ordens.length
              ? true
              : selecionadas.size > 0
                ? "indeterminate"
                : false
          }
          disabled={isLoading}
          onCheckedChange={(checked) =>
            setSelecionadas(checked ? new Set(ordens) : new Set())
          }
          aria-label={t("ordens.selectAll")}
        />
      ),
      cell: ({ row }) => {
        const nome = row.original["nome-da-ordem"];
        return (
          <Checkbox
            checked={selecionadas.has(nome)}
            disabled={isLoading}
            onCheckedChange={(checked) =>
              toggleSelecionada(nome, checked === true)
            }
            aria-label={t("ordens.selectOrder")}
          />
        );
      },
    },
    {
      accessorKey: "nome-da-ordem",
      header: t("ordens.orderName"),
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.original["nome-da-ordem"]}</span>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("ordens.actions")}</div>,
      cell: ({ row }) => {
        const ordem = row.original;
        return (
          <div className="flex items-center gap-2 justify-end">
            <Button
              disabled={isLoading}
              variant="outline"
              size="sm"
              onClick={() => abrirJson(ordem["nome-da-ordem"])}
            >
              <Braces className="h-3.5 w-3.5" />
              {t("ordens.viewJson")}
            </Button>
            <Button
              disabled={isLoading}
              size="sm"
              onClick={() => {
                navigate(`/importar-ordem/${ordem["nome-da-ordem"]}`);
              }}
            >
              <Printer className="h-3.5 w-3.5" />
              {t("ordens.print")}
            </Button>
            <Button
              disabled={isLoading}
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
              onClick={() => handleApagarOrdem(ordem["nome-da-ordem"])}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("ordens.delete")}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <DialogoAlerta
        description={descricaoApagar}
        title={tituloApagar}
        isOpen={alvoApagar !== null}
        onConfirm={confirmarApagar}
        onOpenChange={(open) => {
          if (!open) setAlvoApagar(null);
        }}
        key="dialogo-alerta-apagar-ordem-de-impressao"
      />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("ordens.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("ordens.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selecionadas.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
              onClick={() => setAlvoApagar({ tipo: "selecionadas" })}
              disabled={isLoading}
            >
              <Trash2 className="h-4 w-4" />
              {t("ordens.deleteSelected")} ({selecionadas.size})
            </Button>
          )}
          {ordens.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
              onClick={() => setAlvoApagar({ tipo: "todas" })}
              disabled={isLoading}
            >
              <Trash2 className="h-4 w-4" />
              {t("ordens.deleteAll")}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={abrirImpressas}
            disabled={isLoading}
          >
            <CheckCircle2 className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("ordens.printedOrders")}
            </span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("ordens.sync")}</span>
          </Button>
        </div>
      </div>

      {/* Card com tabela */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">
                {t("ordens.available")}
              </CardTitle>
            </div>
            <Badge variant="secondary" className="font-medium">
              {t("ordens.ordersCount", { count: ordens.length })}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {ordens.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-6 w-6" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-medium text-foreground">
                  {t("ordens.noOrders")}
                </p>
                <p className="text-xs">{t("ordens.noOrdersDesc")}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={sincronizar}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
                {t("ordens.syncNow")}
              </Button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={ordens.map((ordem, index) => ({
                id: index.toString(),
                "nome-da-ordem": ordem,
              }))}
            />
          )}
        </CardContent>
      </Card>

      {/* JSON puro da ordem */}
      <Dialog
        open={ordemJson !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOrdemJson(null);
            setConteudoJson("");
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Braces className="h-4 w-4" />
              {t("ordens.jsonTitle")}
            </DialogTitle>
            <DialogDescription className="break-all">
              {ordemJson}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto rounded-md border bg-muted/40">
            {carregandoJson ? (
              <div className="py-12 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <pre className="p-3 text-xs whitespace-pre font-mono">
                {conteudoJson}
              </pre>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={copiarJson}
              disabled={carregandoJson || !conteudoJson}
            >
              <Copy className="h-4 w-4" />
              {t("ordens.copyJson")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ordens ja marcadas como impressas */}
      <Dialog open={impressasAberto} onOpenChange={setImpressasAberto}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {t("ordens.printedOrdersTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("ordens.printedOrdersDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto">
            {isLoading ? (
              <div className="py-12 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : ordensImpressas.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <Inbox className="h-6 w-6" />
                <p className="text-sm">{t("ordens.noPrintedOrders")}</p>
              </div>
            ) : (
              <ul className="divide-y">
                {ordensImpressas.map((ordem) => (
                  <li
                    key={ordem}
                    className="flex items-center justify-between gap-2 py-2"
                  >
                    <span className="text-sm break-all">{ordem}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => abrirJson(ordem)}
                    >
                      <Braces className="h-3.5 w-3.5" />
                      {t("ordens.viewJson")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Badge variant="secondary" className="font-medium mr-auto">
              {t("ordens.ordersCount", { count: ordensImpressas.length })}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={coletarOrdensImpressas}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              {t("ordens.sync")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OrdensDeImpressao;
